import React from 'react';
import { C } from '../constants';
import { isTecnico } from '../services/auth';

// ─────────────────────────────────────────────────────────────────────────────
// MIS COTIZACIONES (cliente, audiencia 'user') — el cliente VISUALIZA sus estudios
// y tiene ACCIONES sobre ellos: ver el detalle, ACEPTAR la propuesta, SOLICITAR
// ajustes y contactar. El cliente ENVÍA la solicitud y RECIBE respuesta del ingeniero;
// el envío por WhatsApp/correo de la propuesta es acción del ADMIN, no del cliente.
// ─────────────────────────────────────────────────────────────────────────────

const fmtCOP = n => n != null ? '$' + Math.round(n).toLocaleString('es-CO') : '—';
const fmtFecha = (q) => {
  const raw = q.date || q.created_at || q.fecha || q.createdAt;
  const d = raw ? new Date(raw) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString('es-CO') : 'Reciente';
};

// Estados del ciclo, desde la óptica del CLIENTE.
const ESTADO = {
  nuevo:       { label: 'Enviada · en revisión', col: C.teal },
  enviada:     { label: 'Propuesta recibida',    col: C.amber },
  propuesta:   { label: 'Propuesta recibida',    col: C.amber },
  negociacion: { label: 'En ajustes',            col: C.amber },
  ganada:      { label: 'Aceptada',              col: '#1a7f3c' },
  cerrada:     { label: 'Aceptada',              col: '#1a7f3c' },
  perdida:     { label: 'Cerrada',               col: '#c0392b' },
};

export default function MisCotizaciones({ quotes = [], claims, go, updQuote, onAceptar, sincro = 'ok' }) {
  const email = (claims?.email || '').toLowerCase().trim();
  const esTec = isTecnico(claims);
  const tid = claims?.technician_id ?? claims?.user_id ?? claims?.id;
  const mias = esTec
    ? quotes.filter((q) => {
      const qid = q.technician_id ?? q.tecnicoId;
      const qem = String(q.technician_email || q.tecnicoEmail || '').toLowerCase();
      return (tid != null && qid != null && String(qid) === String(tid)) || (email && qem === email);
    })
    : quotes.filter(q => (q.email || '').toLowerCase().trim() === email);
  const [abierta, setAbierta] = React.useState(null);   // id expandido
  const [ajusteFor, setAjusteFor] = React.useState(null); // id con form de ajuste abierto
  const [ajusteText, setAjusteText] = React.useState('');

  const wrap = { maxWidth: 1120, margin: '0 auto', padding: '20px 22px 40px' };
  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', marginBottom: 12 };

  const aceptar = (q) => onAceptar ? onAceptar(q) : (updQuote && updQuote({ ...q, status: 'ganada', cliente_acepto_en: new Date().toISOString() }));
  const enviarAjuste = (q) => {
    const nota = ajusteText.trim();
    if (!nota) return;
    updQuote && updQuote({ ...q, status: 'negociacion', cliente_nota: nota });
    setAjusteFor(null); setAjusteText('');
  };

  const Row = ({ k, v }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5, padding: '4px 0', borderBottom: `1px solid ${C.border}33` }}>
      <span style={{ color: C.muted }}>{k}</span>
      <span style={{ color: C.text, fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{ borderLeft: `3px solid ${C.teal}`, paddingLeft: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.heading }}>{esTec ? 'Seguimiento de cotizaciones' : 'Mis cotizaciones'}</h1>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          {esTec
            ? 'Acompaña las propuestas asignadas: estado, cliente y siguiente paso para cerrar.'
            : 'Consulta tus estudios, acepta la propuesta o solicita ajustes. Un ingeniero te responde.'}
        </div>
      </div>

      {/* 🔴 28-oct: sin sesión firmada lo que se ve es SOLO lo guardado en este equipo. Callarlo es
          perder información en silencio: el cliente cree ver su historial y no está en el espejo. */}
      {sincro === 'sin_sesion' && (
        <div data-aviso-sin-sesion style={{ ...card, borderColor: `${C.amber}66`, background: `${C.amber}12`, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#7A4A00', marginBottom: 4 }}>Entra con Google para ver todas tus cotizaciones</div>
          <div style={{ fontSize: 12, color: '#7A4A00', lineHeight: 1.6 }}>
            Con esta sesión solo ves las que guardaste en este equipo. Entrando con Google (o desde la app
            FluxAI) ves las de todos tus equipos.
          </div>
        </div>
      )}

      {/* 🔴 Con la lista vacía hay DOS mundos distintos y decir el equivocado tiene costo: si la
          consulta al servidor falló, «Aún no tienes cotizaciones» le está negando al titular un
          historial que sí existe —y lo manda a cotizar de nuevo—. Solo se afirma ausencia cuando
          el servidor CONTESTÓ. Mientras la sincronización va, tampoco se afirma nada. */}
      {mias.length === 0 && sincro === 'fallo' ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 22px', borderColor: `${C.amber}66`, background: `${C.amber}12` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#7A4A00', marginBottom: 6 }}>No se pudo cargar tu historial</div>
          <div style={{ fontSize: 12, color: '#7A4A00', marginBottom: 18, lineHeight: 1.6 }}>
            La consulta al servidor no respondió, así que <strong>esto no significa que no tengas
            cotizaciones</strong>. Vuelve a intentarlo en unos minutos; nada se ha perdido.
          </div>
          <button onClick={() => window.location.reload()} style={{ padding: '11px 26px', background: C.teal, color: C.heading, border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            Reintentar
          </button>
        </div>
      ) : mias.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 22px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.heading, marginBottom: 6 }}>
            {sincro === 'pendiente' ? 'Cargando tus cotizaciones…'
              : sincro === 'sin_sesion' ? 'No hay cotizaciones guardadas en este equipo' : 'Aún no tienes cotizaciones'}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 18, lineHeight: 1.6 }}>Genera tu primer estudio solar con precios reales del mercado colombiano.</div>
          <button onClick={() => go && go('quoter')} style={{ padding: '11px 26px', background: C.teal, color: C.heading, border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            Calcular mi sistema solar →
          </button>
        </div>
      ) : mias.map(q => {
        const est = ESTADO[q.status] || ESTADO.nuevo;
        const open = abierta === q.id;
        const aceptada = q.status === 'ganada' || q.status === 'cerrada';
        const r = q.results || {}, b = q.budget || {};
        return (
          <div key={q.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.heading, textTransform: 'capitalize' }}>{esTec && q.name ? `${q.name} · ` : ''}{q.systemType || 'Sistema solar'} · {r.actKwp ?? '—'} kWp</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  #{String(q.id).slice(-6)} · {fmtFecha(q)} · {r.numPanels ?? '—'} paneles · cobertura {r.cov ?? '—'}% · {q.operator || ''}
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: est.col, background: `${est.col}1a`, border: `1px solid ${est.col}55`, borderRadius: 12, padding: '3px 11px', whiteSpace: 'nowrap' }}>{est.label}</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Inversión estimada</span>
              <span style={{ fontSize: 18, fontWeight: 900, color: C.yellow }}>{fmtCOP(b.tot)}</span>
            </div>

            {/* Detalle (ver) */}
            {open && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: C.dark, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                <Row k="Capacidad instalada" v={`${r.actKwp ?? '—'} kWp`} />
                <Row k="Módulos" v={`${r.numPanels ?? '—'} paneles`} />
                <Row k="Producción estimada" v={`${r.mp ? Math.round(r.mp).toLocaleString('es-CO') : '—'} kWh/mes`} />
                <Row k="Cobertura del consumo" v={`${r.cov ?? '—'}%`} />
                {b.sav != null && <Row k="Ahorro anual estimado" v={fmtCOP(b.sav)} />}
                {b.roi != null && <Row k="Retorno de inversión" v={`${b.roi} años`} />}
                {r.co2 != null && <Row k="CO₂ evitado" v={`${Math.round(r.co2).toLocaleString('es-CO')} kg/año`} />}
                <Row k="Operador de red" v={q.operator || '—'} />
                {q.cliente_nota && <div style={{ fontSize: 10.5, color: C.amber, marginTop: 6 }}>Tu solicitud de ajuste: “{q.cliente_nota}”</div>}
              </div>
            )}

            {/* Acciones del CLIENTE */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <button onClick={() => setAbierta(open ? null : q.id)} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.heading, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                {open ? 'Ocultar detalle' : 'Ver detalle'}
              </button>
              {q.tech_token && (
                <a href={`/?view=tracking&token=${q.tech_token}`} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.teal}55`, color: C.teal, fontWeight: 700, fontSize: 12, textDecoration: 'none' }}>Ver seguimiento →</a>
              )}
              {!esTec && !aceptada && q.status !== 'perdida' && (
                <>
                  <button onClick={() => aceptar(q)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: C.teal, color: C.heading, fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>Aceptar propuesta</button>
                  <button onClick={() => { setAjusteFor(ajusteFor === q.id ? null : q.id); setAjusteText(''); }} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.amber}66`, background: ajusteFor === q.id ? `${C.amber}14` : 'transparent', color: C.amber, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Solicitar ajustes</button>
                </>
              )}
              {esTec && (
                <button onClick={() => go && go('ordenes')} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.teal}55`, background: 'transparent', color: C.teal, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  Ir a órdenes / PQR →
                </button>
              )}
              {aceptada && !esTec && (
 <span style={{ fontSize: 11.5, color: '#1a7f3c', fontWeight: 700, alignSelf: 'center' }}>✓ Propuesta aceptada — se programa tu instalación.</span>
              )}
            </div>

            {/* Formato in-app (sin diálogo nativo) para solicitar ajustes */}
            {ajusteFor === q.id && (
              <div style={{ marginTop: 10, padding: '12px 14px', background: C.dark, border: `1px solid ${C.amber}55`, borderRadius: 9 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 6 }}>¿Qué te gustaría ajustar de tu propuesta?</label>
                <textarea value={ajusteText} onChange={e => setAjusteText(e.target.value)} autoFocus rows={3}
                  placeholder="Ej.: quiero más respaldo de baterías, o ajustar el presupuesto…"
                  style={{ width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: '9px 11px', color: C.text, fontSize: 12.5, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={() => enviarAjuste(q)} disabled={!ajusteText.trim()} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: !ajusteText.trim() ? `${C.muted}33` : C.amber, color: !ajusteText.trim() ? C.muted : '#1a1205', fontWeight: 800, fontSize: 12, cursor: !ajusteText.trim() ? 'default' : 'pointer' }}>Enviar al ingeniero</button>
                  <button onClick={() => { setAjusteFor(null); setAjusteText(''); }} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
